/* eslint-disable no-underscore-dangle */
import { v4 as uuidv4 } from "uuid";

export interface HierarchicalMetric {
  id: string;
  parentId?: string;
  name: string;
  totalDuration: number; // duration in ms, sum of children or self if leaf
  selfDuration: number; // duration in ms of this specific task
  startTime: number; // DOMHighResTimeStamp, relative to performance.timeOrigin
  context?: Record<string, any>;
  children: HierarchicalMetric[];
  _internalPerformanceMarkName?: string; // For debugging, actual mark name used with performance API
}

// Stores only root-level metrics. The full tree is built via children arrays.
const rootMetricsStore: HierarchicalMetric[] = [];
// Stores all metric objects by their unique id for quick lookup and tree building.
const allMetricsMap = new Map<string, HierarchicalMetric>();
// Tracks currently active metrics to determine parent-child relationships.
const activeMetricStack: Array<{
  metricId: string;
  performanceMarkName: string;
}> = [];
// Track metrics that have been stopped to prevent double-stopping
const stoppedMetrics = new Set<string>();

// Helper function to recalculate totalDuration for a metric and propagate upwards
const recalculateAndPropagateTotalDuration = (
  metricId: string | undefined
): void => {
  if (!metricId) return;
  const metricToUpdate = allMetricsMap.get(metricId);

  if (!metricToUpdate) {
    console.warn(
      `recalculateAndPropagateTotalDuration: Metric with id "${metricId}" not found.`
    );
    return;
  }

  if (metricToUpdate.children && metricToUpdate.children.length > 0) {
    let sumOfChildrenTotalDurations = 0;
    metricToUpdate.children.forEach((child) => {
      sumOfChildrenTotalDurations +=
        typeof child.totalDuration === "number" && child.totalDuration >= 0 // Allow 0 duration
          ? child.totalDuration
          : 0;
    });
    // Ensure selfDuration is non-negative. It should be correctly calculated in stop().
    const currentSelfDuration =
      typeof metricToUpdate.selfDuration === "number" &&
      metricToUpdate.selfDuration >= 0
        ? metricToUpdate.selfDuration
        : 0;
    metricToUpdate.totalDuration =
      currentSelfDuration + sumOfChildrenTotalDurations;
  } else {
    // It's a leaf node. Its totalDuration should be its selfDuration.
    // selfDuration should be correctly calculated in its stop() call.
    metricToUpdate.totalDuration =
      typeof metricToUpdate.selfDuration === "number" &&
      metricToUpdate.selfDuration >= 0
        ? metricToUpdate.selfDuration
        : 0;
  }

  // Propagate update to its parent
  if (metricToUpdate.parentId) {
    recalculateAndPropagateTotalDuration(metricToUpdate.parentId);
  }
};

export const performanceMonitor = {
  start: (
    metricName: string,
    initialContext?: Record<string, any>,
    options?: { parentId?: string | null }
  ): string => {
    const metricId = uuidv4();
    // Using metricId in performanceMarkName ensures it's unique for performance.mark
    const performanceMarkName = `${metricName}_${metricId}_start`;
    performance.mark(performanceMarkName);

    let determinedParentId: string | undefined;
    if (options && typeof options.parentId === "string") {
      determinedParentId = options.parentId;
    } else if (options && options.parentId === null) {
      // Force root if parentId is null
      determinedParentId = undefined;
    } else if (activeMetricStack.length > 0) {
      // Default: use active stack
      determinedParentId =
        activeMetricStack[activeMetricStack.length - 1].metricId;
    } else {
      // Default: root if stack is empty or no override
      determinedParentId = undefined;
    }

    const newMetric: HierarchicalMetric = {
      id: metricId,
      parentId: determinedParentId,
      name: metricName,
      startTime: performance.now(), // Approximate start, will be refined by performance.measure entry
      totalDuration: -1, // Placeholder, will be set on stop
      selfDuration: -1, // Placeholder, will be set on stop
      context: initialContext,
      children: [],
      _internalPerformanceMarkName: performanceMarkName
    };

    allMetricsMap.set(metricId, newMetric);
    activeMetricStack.push({ metricId, performanceMarkName });

    return metricId; // Return our internal metricId to the caller
  },

  stop: (metricId: string, finalContext?: Record<string, any>): boolean => {
    // Check if already stopped to prevent double-stopping
    if (stoppedMetrics.has(metricId)) {
      return false; // Already stopped, silently ignore
    }

    const metricFromMap = allMetricsMap.get(metricId);

    if (!metricFromMap || !metricFromMap._internalPerformanceMarkName) {
      console.warn(
        `Performance metric with id "${metricId}" not found or not properly started.`
      );
      return false;
    }

    // Mark as stopped early to prevent double-stopping
    stoppedMetrics.add(metricId);

    // Stack management: Find and remove the metric from stack
    const stackIndex = activeMetricStack.findIndex(
      (s) => s.metricId === metricId
    );

    if (stackIndex === activeMetricStack.length - 1) {
      // Normal LIFO case - metric is at the top of the stack
      activeMetricStack.pop();
    } else if (stackIndex > -1) {
      // Out-of-order completion - remove from middle of stack
      // This is normal in async environments and shouldn't trigger warnings
      activeMetricStack.splice(stackIndex, 1);
    } else {
      // Metric not found in stack - this is unusual but not critical
      console.warn(
        `Performance metric "${metricId}" not found in active stack. This may indicate improper cleanup.`
      );
    }

    const performanceMeasureName = `${metricFromMap.name}_${metricId}_measure`;
    let ownInclusiveDuration = -1;

    try {
      // Measure the metric's own duration and get precise startTime
      performance.measure(
        performanceMeasureName,
        // eslint-disable-next-line no-underscore-dangle
        metricFromMap._internalPerformanceMarkName
      );
      const entry = performance
        .getEntriesByName(performanceMeasureName, "measure")
        .pop();

      if (entry) {
        ownInclusiveDuration = entry.duration;
        metricFromMap.startTime = entry.startTime; // Update with more precise startTime
      } else {
        // Fallback if measure entry isn't found
        ownInclusiveDuration =
          performance.now() - (metricFromMap.startTime || performance.now()); // Use existing approximate startTime
      }

      // Calculate true selfDuration based on ownInclusiveDuration and sum of children's totalDurations
      let sumOfProcessedChildrenTotalDurations = 0;
      metricFromMap.children.forEach((child) => {
        // Children in this list have already stopped, and their totalDuration should be their final, rolled-up value.
        sumOfProcessedChildrenTotalDurations +=
          typeof child.totalDuration === "number" && child.totalDuration >= 0
            ? child.totalDuration
            : 0;
      });
      metricFromMap.selfDuration = Math.max(
        0,
        ownInclusiveDuration - sumOfProcessedChildrenTotalDurations
      );

      // Update context
      if (finalContext) {
        metricFromMap.context = { ...metricFromMap.context, ...finalContext };
      }
    } catch (e) {
      console.error(
        `Error measuring performance for "${metricFromMap.name}" (id: "${metricId}"):`,
        e
      );
      // Attempt to set a fallback duration and update context even on error
      ownInclusiveDuration =
        performance.now() - (metricFromMap.startTime || performance.now());
      // Fallback selfDuration calculation if error occurred during measurement
      let sumOfProcessedChildrenTotalDurationsOnError = 0;
      metricFromMap.children.forEach((child) => {
        sumOfProcessedChildrenTotalDurationsOnError +=
          typeof child.totalDuration === "number" && child.totalDuration >= 0
            ? child.totalDuration
            : 0;
      });
      metricFromMap.selfDuration = Math.max(
        0,
        ownInclusiveDuration - sumOfProcessedChildrenTotalDurationsOnError
      );

      if (finalContext) {
        metricFromMap.context = { ...metricFromMap.context, ...finalContext };
      }
      // If measurement failed, totalDuration defaults to this (potentially 0) selfDuration.
      metricFromMap.totalDuration = metricFromMap.selfDuration;
    } finally {
      // Always clear marks and measures for this metric
      if (metricFromMap._internalPerformanceMarkName) {
        performance.clearMarks(metricFromMap._internalPerformanceMarkName);
      }
      performance.clearMeasures(performanceMeasureName);
    }

    // The initial assignment of totalDuration if it wasn't set during an error.
    // recalculateAndPropagateTotalDuration will correctly set the final value.
    if (metricFromMap.totalDuration === -1) {
      metricFromMap.totalDuration = metricFromMap.selfDuration;
    }

    // Add to parent's children array or to rootMetricsStore
    if (metricFromMap.parentId) {
      const parentMetric = allMetricsMap.get(metricFromMap.parentId);
      if (parentMetric) {
        // Add current metric (metricFromMap) to its parent's children list
        if (
          !parentMetric.children.some((child) => child.id === metricFromMap.id)
        ) {
          parentMetric.children.push(metricFromMap);
        }
        parentMetric.children.sort((a, b) => a.startTime - b.startTime);
        // DO NOT call recalculateAndPropagateTotalDuration for parentMetric.id here.
        // It will be handled by the call for metricFromMap.id below, which propagates upwards.
      } else {
        // Parent not found, treat as root
        console.warn(
          `Parent metric with id "${metricFromMap.parentId}" not found for child "${metricId}". Adding as root.`
        );
        if (
          !rootMetricsStore.some((metric) => metric.id === metricFromMap.id)
        ) {
          rootMetricsStore.push(metricFromMap);
          rootMetricsStore.sort((a, b) => a.startTime - b.startTime);
        }
        // Ensure it's treated as a root for propagation
        metricFromMap.parentId = undefined;
      }
    } else if (
      !rootMetricsStore.some((metric) => metric.id === metricFromMap.id)
    ) {
      // It's a root metric and not already added (e.g. if parentId was initially undefined)
      rootMetricsStore.push(metricFromMap);
      rootMetricsStore.sort((a, b) => a.startTime - b.startTime);
    }

    // Now that selfDuration is final and the metric is linked in the tree,
    // calculate its final totalDuration and propagate the update upwards.
    recalculateAndPropagateTotalDuration(metricFromMap.id);

    return true; // Successfully stopped
  },

  getMetrics: (): ReadonlyArray<HierarchicalMetric> => [...rootMetricsStore],

  clearMetrics: (): void => {
    rootMetricsStore.length = 0;
    allMetricsMap.clear();
    activeMetricStack.length = 0;
    stoppedMetrics.clear();
    // It's good practice to also clear any remaining marks/measures from the browser's performance buffer
    // though our stop() method attempts to clean up its own.
    // performance.clearMarks(); // Clears ALL marks
    // performance.clearMeasures(); // Clears ALL measures
  },

  measureSync: <T extends (...args: any[]) => any>(
    metricName: string,
    fn: T,
    context?: Record<string, any>,
    options?: { parentId?: string | null }
  ): ReturnType<T> => {
    const metricId = performanceMonitor.start(metricName, context, options);
    try {
      return fn();
    } finally {
      performanceMonitor.stop(metricId); // Pass only metricId
    }
  },

  measureAsync: async <T>(
    metricName: string,
    promiseFn: () => Promise<T>,
    context?: Record<string, any>,
    options?: { parentId?: string | null }
  ): Promise<T> => {
    const metricId = performanceMonitor.start(metricName, context, options);
    try {
      return await promiseFn();
    } finally {
      performanceMonitor.stop(metricId); // Pass only metricId
    }
  }
};

// Example (Conceptual - not run here):
// const funcA = () => {
//   performanceMonitor.measureSync("FuncA", () => {
//     console.log("FuncA running");
//     funcB();
//     performanceMonitor.measureSync("FuncA-ChildSync", () => {
//       console.log("FuncA-ChildSync running");
//     });
//   });
// };
// const funcB = () => {
//   performanceMonitor.measureSync("FuncB", () => {
//     console.log("FuncB running");
//   });
// };
// funcA();
// console.log(JSON.stringify(performanceMonitor.getMetrics(), null, 2));
